import { Directive, AfterViewInit, OnDestroy, Input, Self, NgZone, Output, EventEmitter } from '@angular/core';
import { CdkDrag, Point } from '@angular/cdk/drag-drop';
import { fromEvent, timer } from 'rxjs';
import { debounceTime, filter, finalize } from 'rxjs/operators';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { SkyLocalStorageService } from '@skyeng/libs/shared/local-storage';

const FLOATING_STORAGE_KEY = 'menuPopupFloating';

interface StoredFloatingPositions {
  [name: string]: Point;
}

const DEFAULT_FLOATING_POSITION: Point = { x: 0, y: 0 };

@DecorateUntilDestroy()
@Directive({
  selector: '[skyUiPopoverDragPosition]',
})
export class DragPositionDirective implements AfterViewInit, OnDestroy {
  @Input('skyUiPopoverDragPosition') name?: string;
  @Output('skyUiPopoverDragpositionChanged') positionChanged = new EventEmitter<void>();

  constructor(
    private skyLocalStorageService: SkyLocalStorageService,
    private ngZone: NgZone,
    @Self() private cdkDrag: CdkDrag,
  ) {}

  ngAfterViewInit(): void {
    if (!this.name) {
      return;
    }

    this.applySavedPosition();
    this.onWindowResizeUpdate();

    this.cdkDrag.ended.pipe(takeUntilDestroyed(this)).subscribe(() => this.updateSavedPosition());
  }

  ngOnDestroy(): void {}

  private applySavedPosition(): void {
    if (this.name) {
      const savedPosition = this.getFloatingPositionFromStorage(this.name);
      this.applyPosition(savedPosition);
    }
  }

  private resetPosition(): void {
    this.applyPosition(DEFAULT_FLOATING_POSITION);

    if (this.name) {
      this.saveFloatingPositionToStorage(this.name, DEFAULT_FLOATING_POSITION);
    }
  }

  private applyPosition(position: Point): void {
    // Таймер нужен чтобы перезаписать создаваемый overlay'ем translate: transform.
    timer(0)
      .pipe(
        finalize(() => this.positionChanged.emit()),
        takeUntilDestroyed(this),
      )
      .subscribe(() => {
        /**
         * Простое присвоение в `freeDragPosition` не обновляет внутреннее состояние cdkDrag
         * если оно изменено из вне (в нашем случае из-за собственной трансформации flexibleConnectedTo)
         */
        // eslint-disable-next-line no-underscore-dangle
        this.cdkDrag._dragRef.setFreeDragPosition(position);

        const isInViewPort = this.isInViewport(this.cdkDrag.element.nativeElement);
        if (!isInViewPort) {
          // eslint-disable-next-line no-underscore-dangle
          this.cdkDrag._dragRef.setFreeDragPosition(DEFAULT_FLOATING_POSITION);
        }
      });
  }

  private updateSavedPosition(): void {
    const dragPosition = this.cdkDrag.getFreeDragPosition();

    if (this.name) {
      this.saveFloatingPositionToStorage(this.name, dragPosition);
    }
  }

  private getFloatingPositionFromStorage(name: string): Point {
    const positions = this.skyLocalStorageService.get<StoredFloatingPositions>(FLOATING_STORAGE_KEY, {});

    return positions[name] || DEFAULT_FLOATING_POSITION;
  }

  private saveFloatingPositionToStorage(name: string, position: Point): void {
    const positions = this.skyLocalStorageService.get<StoredFloatingPositions>(FLOATING_STORAGE_KEY, {});

    // eslint-disable-next-line functional/immutable-data
    positions[name] = position;

    this.skyLocalStorageService.set(FLOATING_STORAGE_KEY, positions);
  }

  /**
   * Из-за flexibleConnectedTo окошко при ресайзах коннектится
   * обратно к инициатору (в нашем случае кнопки открытия),
   * перетирая реальное значение позиции transform: translate.
   * При этом и наше сохраненное значение в localStorage и cdkDrag
   * ничего об этом не зная, в следующий dragStart или при новой инициализации
   * берут не реальную позицию, а сохраненную (из localStorage или freeDragPosition).
   * Поэтому сбрасываем позицию после ресайза вручную.
   */
  private onWindowResizeUpdate(): void {
    this.ngZone.runOutsideAngular(() =>
      fromEvent(window, 'resize')
        .pipe(
          debounceTime(200),
          filter(() => !!this.name),
          takeUntilDestroyed(this),
        )
        .subscribe(() => this.resetPosition()),
    );
  }

  private isInViewport(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }
}
